import {useCallback, useState} from 'react';
import axios from 'axios';

import type {AxiosResponse} from 'axios';

import {DEFAULT_APP_CONTEXT} from '~/types/app-context-type';

import type {AppI18n, AppI18nHookData} from '~/types/i18n-types';

export const useAppI18n = (ctx: string): AppI18nHookData => {
    const i18nStr: string = window.localStorage.getItem('tn.erpui.i18n');

    let storedI18n: AppI18n = null;

    if (i18nStr !== null) {
        storedI18n = JSON.parse(i18nStr);
    }

    const [fetchingLocale, setFetchingLocale] = useState<boolean>(false);
    const [fetchingTranslations, setFetchingTranslations] = useState<boolean>(false);
    const [i18n, setI18n] = useState<AppI18n>(storedI18n);

    const fetchLocale = useCallback(async (name: string): Promise<{[key: string]: string}> => {
        setFetchingLocale(true);

        return axios.get<{[key: string]: string}>(`/${ctx}/lang/${name}.json`)
            .then((response: AxiosResponse<{[key: string]: string}>) => {
                if (typeof response.data !== 'undefined') {
                    setI18n((curr: AppI18n) => {
                        const newI18n = curr === null
                            ? {
                                ...DEFAULT_APP_CONTEXT.i18n,
                                messages: {
                                    ...DEFAULT_APP_CONTEXT.i18n.messages,
                                    [name]: response.data,
                                },
                            }
                            : {
                                ...curr,
                                messages: {
                                    ...curr.messages,
                                    [name]: response.data,
                                },
                            };

                        window.localStorage.setItem('tn.erpui.i18n', JSON.stringify(newI18n));

                        return newI18n;
                    });

                    return response.data;
                }

                return null;
            })
            .finally(() => {
                setFetchingLocale(false);
            });
    }, [ctx]);

    const fetchTranslations = useCallback(async (): Promise<void> => {
        setFetchingTranslations(true);

        return axios.get<string[]>(`/${ctx}/lang/translations.json`)
            .then((response: AxiosResponse<string[]>) => {
                if (typeof response.data !== 'undefined') {
                    setI18n((curr: AppI18n) => {
                        const newI18n = curr === null
                            ? {...DEFAULT_APP_CONTEXT.i18n, translations: response.data}
                            : {...curr, translations: response.data};

                        window.localStorage.setItem('tn.erpui.i18n', JSON.stringify(newI18n));

                        return newI18n;
                    });
                }
            })
            .finally(() => {
                setFetchingTranslations(false);
            });
    }, [ctx]);

    return {
        i18n,
        fetchLocale,
        fetchTranslations,
        fetchingLocale,
        fetchingTranslations,
    };
};

export default useAppI18n;
